import * as R from 'ramda';
import { ActionType } from '../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../core/api/module';
import { Endpoints } from '../../core/api/endpoints';
import { ReportDeliveryRequest } from '../models';
import { toMap } from '../../language/arrays';


const KEY_ACTION = ActionType.DeliverReportsToRecipientRequest;
const ACTIONS    = [
  ActionType.DeliverReportsToRecipientRequest,
  ActionType.DeliverReportsToRecipientResponse,
  ActionType.DeliverReportsToRecipientError,
];


type Payload = string;


type Meta = {
  deliveryRequest: ReportDeliveryRequest[];

};


/**
 * Dispatch this to retrieve all the participants whose raters the current user has been assigned to approve
 */
export const deliverReportsAction = (
  deliveryRequest: ReportDeliveryRequest[],
  recipientId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DeliverReportsToRecipient,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      reports: deliveryRequest,
      recipientId: recipientId,
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { deliveryRequest }),
    bailout:  state => !!getLoading(state, KEY_ACTION),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.deliveryRequest}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const allReports = toMap(
    state.reportRecipientReports || [],
    r => `${r.reportSetId}-${r.participantId}-${r.reportId}`,
  );
  const requestArray = action.meta.deliveryRequest;
  const requestMap   = toMap(requestArray, req => `${req.reportSetId}-${req.participantId}-${req.reportId}`);
  const reportsToUpdate = R.map(key => allReports[key], Object.keys(requestMap));
  const updatedReports = R.map(
    r => R.mergeRight(r, { deliveryDate: new Date(), portalDelivered: true }), reportsToUpdate,
  );
  const reMappedUpdatedReports = toMap(
    updatedReports,
    r => `${r.reportSetId}-${r.participantId}-${r.reportId}`,
  );
  const merged = R.mergeRight(allReports, reMappedUpdatedReports);

  return R.assoc('reportRecipientReports', Object.values(merged), successState);
};

export const deliverReportsToRecipientReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
